import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Users, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface SendCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  onCampaignSent?: () => void;
}

interface InactiveClient {
  client_id: string;
  first_name: string;
  phone_normalized: string;
  days_since_booking: number;
}

export function SendCampaignDialog({
  open,
  onOpenChange,
  businessId,
  onCampaignSent
}: SendCampaignDialogProps) {
  const [message, setMessage] = useState("");
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [recipients, setRecipients] = useState<InactiveClient[]>([]);
  const [bookingUrl, setBookingUrl] = useState<string>("");

  // Load eligible recipients when dialog opens
  useEffect(() => {
    if (open && businessId) {
      loadRecipients();
    } else {
      // Reset state when dialog closes
      setMessage("");
      setRecipients([]);
    }
  }, [open, businessId]);

  const loadRecipients = async () => {
    setIsLoadingRecipients(true);
    try {
      // Load recipients and booking URL in parallel
      const [recipientsResult, businessResult] = await Promise.all([
        supabase.rpc('get_inactive_clients', {
          p_business_id: businessId,
          p_inactive_days: 21,
          p_max_results: 25,
          p_campaign_cooldown_days: 30
        }),
        supabase
          .from('business_profiles')
          .select('booking_url')
          .eq('id', businessId)
          .single()
      ]);

      if (recipientsResult.error) throw recipientsResult.error;
      if (businessResult.error) throw businessResult.error;

      setRecipients(recipientsResult.data || []);
      setBookingUrl(businessResult.data?.booking_url || '');
    } catch (error: any) {
      console.error('Error loading recipients:', error);
      toast.error('Failed to load eligible recipients');
    } finally {
      setIsLoadingRecipients(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    if (recipients.length === 0) {
      toast.error("No eligible recipients found");
      return;
    }

    // Enforce 160-character hard limit (no multi-part SMS allowed)
    if (isMessageTooLong) {
      toast.error("Message too long. SMS messages must stay under 160 characters total.");
      return;
    }

    setIsSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-campaign`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            business_id: businessId,
            message: message.trim()
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send campaign');
      }

      const result = await response.json();

      if (result.failed_count > 0) {
        // Construct specific failure message based on failure types
        let failureMessage = `Sent to ${result.sent_count} of ${result.recipient_count} clients.`;

        if (result.length_overflow_count > 0 && result.length_overflow_count === result.failed_count) {
          // All failures were due to message length
          failureMessage += ` ${result.failed_count} client${result.failed_count !== 1 ? 's were' : ' was'} skipped because the final personalized message was too long.`;
        } else if (result.length_overflow_count > 0) {
          // Some failures were due to length
          failureMessage += ` ${result.length_overflow_count} skipped due to message length, ${result.failed_count - result.length_overflow_count} other failures.`;
        } else {
          // Generic failure message
          failureMessage += ` ${result.failed_count} failed.`;
        }

        toast.warning(failureMessage);
      } else {
        toast.success(
          `Campaign sent successfully to ${result.sent_count} client${result.sent_count !== 1 ? 's' : ''}!`
        );
      }

      onOpenChange(false);
      onCampaignSent?.();
    } catch (error: any) {
      console.error('Error sending campaign:', error);
      toast.error(error.message || 'Failed to send campaign');
    } finally {
      setIsSending(false);
    }
  };

  // Calculate overhead for personalization and auto-appended link
  // SMS hard limit: 160 characters (single SMS, no multi-part allowed)
  // NOTE: This is a UI estimate based on current recipients at dialog open time.
  // Backend re-queries recipients at send time and enforces the 160-char limit server-side.
  const SMS_HARD_LIMIT = 160;
  const bookingLink = bookingUrl ? `https://getfiggy.link/${bookingUrl}` : 'https://getfiggy.link/[your-link]';
  const autoAppendedText = `\n\nBook here: ${bookingLink}`;

  // Use longest first name from recipients for conservative overhead calculation (or "John" as fallback)
  const longestFirstName = recipients.length > 0
    ? recipients.reduce((longest, r) =>
        r.first_name && r.first_name.length > longest.length ? r.first_name : longest
      , recipients[0]?.first_name || "John")
    : "John";

  const personalizationPrefix = `Hi ${longestFirstName}! `;
  const overhead = personalizationPrefix.length + autoAppendedText.length;

  // Max characters user can type (160 - overhead)
  const maxUserMessageLength = Math.max(SMS_HARD_LIMIT - overhead, 50); // Min 50 chars for usability

  // Final rendered message length
  const finalMessageLength = personalizationPrefix.length + message.length + autoAppendedText.length;
  const isMessageTooLong = finalMessageLength > SMS_HARD_LIMIT;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send Reactivation Campaign</DialogTitle>
          <DialogDescription>
            Send an SMS to clients whose last past booking was 21+ days ago
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSend} className="space-y-4">
          {/* Recipients Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            {isLoadingRecipients ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading eligible recipients...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-figgy-500" />
                <span className="font-medium">
                  {recipients.length} eligible client{recipients.length !== 1 ? 's' : ''}
                </span>
                <span className="text-sm text-muted-foreground">
                  (max 25, last booking 21+ days ago)
                </span>
              </div>
            )}
          </div>

          {recipients.length === 0 && !isLoadingRecipients && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No clients are ready for reactivation right now. Clients are excluded if they
                booked recently, already have an upcoming appointment, opted out, or were
                contacted in the last 30 days.
              </AlertDescription>
            </Alert>
          )}

          {isMessageTooLong && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Message too long. SMS messages must stay under 160 characters total
                (including personalization and booking link). Please shorten your message.
              </AlertDescription>
            </Alert>
          )}

          {/* Message Input */}
          <div className="space-y-2">
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Example: We miss you! Would love to see you again soon."
              rows={4}
              required
              disabled={isSending}
              maxLength={maxUserMessageLength}
              className="resize-none"
            />
            <div className="flex justify-between text-xs">
              <span className={message.length > maxUserMessageLength * 0.9 ? "text-amber-600" : "text-muted-foreground"}>
                {message.length} / {maxUserMessageLength} characters
              </span>
              {isMessageTooLong && (
                <span className="text-red-600 font-medium">
                  Message too long (160 char limit)
                </span>
              )}
            </div>
          </div>

          {/* Auto-append notice */}
          <Alert>
            <AlertDescription className="text-sm">
              Your booking link will be automatically added. Messages are personalized
              with each client's first name. Character limit is estimated from current eligible clients.
              Final validation happens when you send.
            </AlertDescription>
          </Alert>

          {/* Preview */}
          {message.trim() && recipients.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Preview (example for {recipients[0]?.first_name || 'client'}):
              </p>
              <p className="text-sm whitespace-pre-wrap">
                {recipients[0]?.first_name ? `Hi ${recipients[0].first_name}! ` : ''}
                {message.trim()}
                {autoAppendedText}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-figgy-500 hover:bg-figgy-600"
              disabled={isSending || recipients.length === 0 || !message.trim() || isMessageTooLong}
            >
              {isSending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                `Send to ${recipients.length} client${recipients.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
